import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PatientReport, { ReportData, ZScoresResponse } from './PatientReport';
import { callQuery } from '../../amplify/appsync/query';
import { 
  COMPASS_PARTICIPANT_DETAILS, 
  COMPASS_ANNOTATIONS_BY_STUDY_ID, 
  COMPASS_SURVEYS_BY_PARTICIPANT_ID 
} from '../../amplify/appsync/query';
import { withTimezone, generateDates, getDayTruncatedDate, subtractHours } from '../../utils/time';
import { callCalculateVitalsAbnormalities } from '../../amplify/appsync/calculate-vitals-abnormalities';

const PrintPage = () => {
  const { studyId } = useParams();
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [zScoresData, setZScoresData] = useState<ZScoresResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handlePrint = () => {
    document.title = `${studyId}_report.pdf`;
    window.print();
  };

  const loadReportData = async (studyId: string) => {
    if (isLoading) {
      return;
    }
    else {
      setIsLoading(true);
    }
    
    setError(null);
    
    try {
      // Load participant details
      const participantInfo = await callQuery({
        operation: COMPASS_PARTICIPANT_DETAILS,
        study_id: studyId,
      });

      if (!participantInfo?.length) {
        throw new Error('No participant data found for this study ID');
      }

      const participant = participantInfo[0];

      // Calculate date range
      const endDate = withTimezone(
        participant.end_date ? new Date(participant.end_date) : new Date(),
        "America/Los_Angeles"
      );
      const tPlus31Date = withTimezone(
        subtractHours(new Date(participant.enrollment_date), -24 * 30),
        "America/Los_Angeles"
      );
      const finalEndDate = endDate > tPlus31Date ? endDate : tPlus31Date;
      const dateRange = generateDates(
        getDayTruncatedDate(withTimezone(new Date(participant.enrollment_date), "America/Los_Angeles")),
        getDayTruncatedDate(finalEndDate)
      );

      // Load annotations
      const annotations = await callQuery({
        operation: COMPASS_ANNOTATIONS_BY_STUDY_ID,
        study_id: studyId,
        granularity: "daily",
      });

      // Process annotations
      const dailyKeyToData: Record<string, Record<string, number>> = {};
      annotations.forEach((row: any) => {
        const dt = withTimezone(new Date(row[0]), "America/Los_Angeles")
          .toISOString()
          .split("T")[0];
        if (row[2] !== 0){
          if (row[1] !== "WEARING_DETECTION" && 
            row[1] !== "VISIT_HOSPITAL" && 
            row[1] !== "VISIT_ED" && 
            row[1] !== "VISIT_NON_HOSPITAL") {
            if (!(row[1] in dailyKeyToData)) {
              dailyKeyToData[row[1]] = {};
            }
            dailyKeyToData[row[1]][dt] = row[2];
          }
        }
      });

      // Load surveys
      const surveys = await callQuery({
        operation: COMPASS_SURVEYS_BY_PARTICIPANT_ID,
        study_id: studyId,
      });

      // Process surveys
      const dateToSurveyAvailability: Record<string, any> = {};
      surveys.forEach((row: any) => {
        const dt = withTimezone(new Date(row.start_date), "America/Los_Angeles")
          .toISOString()
          .split("T")[0];
        dateToSurveyAvailability[dt] = row;
      });

      const newReportData = {
        studyId,
        dailyKeyToData,
        dateToSurveyAvailability,
        compassDates: dateRange,
      };

      setReportData(newReportData);
      sessionStorage.setItem(studyId + 'printReportData', JSON.stringify(newReportData));

      return { dateRange, dailyKeyToData, dateToSurveyAvailability };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while loading the report';
      setError(errorMessage);
      setReportData(null);
      throw error;
    }
  };

  const loadZScoresData = async (
    dateRange: Date[], 
    dailyKeyToData: Record<string, Record<string, number>>, 
    dateToSurveyAvailability: Record<string, any>,
    studyId: string
  ) => {
    try {
      console.log('Fetching z-scores data');
      const csvData = dateRange.map(date => {
        const dateStr = date.toISOString().split('T')[0];
        const surveyData = dateToSurveyAvailability[dateStr] || {};
        
        const dataPoint: Record<string, string | number> = { date: dateStr };
        
        // Process survey fields
        for (const [key, value] of Object.entries(surveyData)) {
          if (value !== undefined && value !== null && value !== '' && !isNaN(Number(value)) && value !== 0) {
            dataPoint[key] = Number(value);
          }
        }

        // Process vital signs
        for (const [metricKey, dateValues] of Object.entries(dailyKeyToData)) {
          const value = dateValues[dateStr];
          const numValue = Number(value);
          if (value !== undefined && value !== null && !isNaN(numValue) && numValue !== 0) {
            dataPoint[metricKey] = numValue;
          }
        }
        
        return dataPoint;
      }).filter(dataPoint => {
        const hasNonDateData = Object.keys(dataPoint).length > 1;
        const hasVitals = Object.keys(dataPoint).some(key => 
          key.includes('PULSE_RATE') || 
          key.includes('RESPIRATORY_RATE') || 
          key.includes('STEP_COUNTS') ||
          key.includes('TEMPERATURE') ||
          key.includes('ACCELEROMETERS')
        );
        const hasSurvey = Object.keys(dataPoint).some(key => 
          key.startsWith('eq_') || 
          key.startsWith('pain_') || 
          key.startsWith('visit_')
        );
        return hasNonDateData && (hasVitals || hasSurvey);
      });

      if (csvData.length === 0) {
        console.log('No valid data points found for abnormal vitals analysis');
        return null;
      }

      const vitalsResponse = await callCalculateVitalsAbnormalities(JSON.stringify({
        csv_data: csvData,
        mrn: studyId
      }));
      
      const parsedVitals = JSON.parse(JSON.parse(vitalsResponse.replace(/NaN/g, 'null')).body);

      if (parsedVitals.response === "Invalid input format") {
        throw new Error('Invalid input format for vitals calculation');
      }
      const zScoresData = parsedVitals.response.z_scores.status === 'insufficient_data' 
        ? null 
        : parsedVitals.response;

      setZScoresData(zScoresData);
      sessionStorage.setItem(studyId + 'printZScoresData', JSON.stringify(zScoresData));

      return zScoresData;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred while calculating z-scores';
      setError(errorMessage);
      setZScoresData(null);
      sessionStorage.removeItem(studyId + 'printZScoresData');
      throw error;
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      if (!studyId) return;

      // Try to get data from sessionStorage first
      // const storedReportData = sessionStorage.getItem(studyId + 'printReportData');
      // const storedZScores = sessionStorage.getItem(studyId + 'printZScoresData');
      let storedReportData = null;
      let storedZScores = null;
      
      let reportDataResult;
      let shouldLoadZScores = true;

      if (storedReportData) {
        try {
          const parsedData = JSON.parse(storedReportData);
          reportDataResult = {
            dateRange: parsedData.compassDates.map((dateStr: string) => new Date(dateStr)),
            dailyKeyToData: parsedData.dailyKeyToData,
            dateToSurveyAvailability: parsedData.dateToSurveyAvailability
          };
          setReportData({
            ...parsedData,
            compassDates: reportDataResult.dateRange
          });
        } catch (error) {
          console.error('Error loading stored report data:', error);
          // If we can't load stored data, we'll need to fetch fresh data
          reportDataResult = await loadReportData(studyId);
        }
      } else {
        // No stored data, load fresh data
        console.log('No stored data, fetch fresh patient data')
        reportDataResult = await loadReportData(studyId);
        setIsLoading(false);
      }

      // Handle z-scores data
      if (storedZScores) {
        try {
          const parsedZScores = JSON.parse(storedZScores);
          setZScoresData(parsedZScores);
          shouldLoadZScores = false;
        } catch (error) {
          console.error('Error loading stored z-scores:', error);
          shouldLoadZScores = true;
        }
      }

      if (shouldLoadZScores && reportDataResult) {
        await loadZScoresData(
          reportDataResult.dateRange,
          reportDataResult.dailyKeyToData,
          reportDataResult.dateToSurveyAvailability,
          studyId
        );
      }

      setIsLoading(false);
    };

    initializeData();
  }, [studyId]);
  return (
    <>
      <style>
        {`
          /* Reset all background colors */
          html, 
          body, 
          #root,
          div[class*="App"],
          main,
          .print-page,
          .print-container {
            background-color: white !important;
          }
          
          .print-page {
            background: white;
            padding: 60px;
            margin-top: 80px;
          }

          .print-container {
            width: 100%;
            max-width: 210mm;
            margin: 0 auto;
          }

          .button-container {
            position: fixed;
            top: 80px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 1000;
            display: flex;
            gap: 12px;
          }

          .print-button {
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
            border-radius: 6px;
            padding: 8px 16px;
            background: white;
            border: 1px solid #e5e7eb;
            cursor: pointer;
          }

          .error-message {
            color: #ef4444;
            text-align: center;
            margin: 2rem 0;
            padding: 1rem;
            border: 1px solid #fee2e2;
            border-radius: 6px;
            background-color: #fef2f2;
          }

          @media print {
            .print-page {
              padding: 0;
              margin-top: 0;
            }

            .button-container,
            .error-message {
              display: none;
            }

            @page {
              size: A4;
              margin: 15mm;
            }

            body {
              margin: 0;
              background: white;
            }
          }

          .chart-container {
            height: 200px !important;
            margin-bottom: 20px;
            page-break-inside: avoid;
          }

          @media print {
            .progress-container {
              display: none !important;
            }
          }
        `}
      </style>
      <div className="button-container">
        <button 
          onClick={handlePrint}
          className="print-button flex items-center gap-2 text-gray-700 hover:text-gray-900"
          disabled={isLoading || !!error}
        >
          <span className="font-medium">
            {isLoading ? 'Loading...' : 'Print Report'}
          </span>
        </button>
      </div>
      <div className="print-page">
        <div className="print-container">
          {error ? (
            <div className="error-message">
              {error}
            </div>
          ) : (
            <PatientReport
              reportData={reportData}
              zScoresData={zScoresData}
              isLoading={isLoading}
              error={error}
              isPrintMode={true}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default PrintPage;